import { useEffect, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import api, { API, apiError } from "@/lib/api";
import { money, dateFr, CSTATUS } from "@/lib/format";
import ContractForm from "@/components/ContractForm";
import AmendDialog from "@/components/AmendDialog";
import SendDialog from "@/components/SendDialog";
import PausesDialog from "@/components/PausesDialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Table, TableHeader, TableRow, TableHead, TableBody, TableCell } from "@/components/ui/table";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator } from "@/components/ui/dropdown-menu";
import {
  AlertDialog, AlertDialogContent, AlertDialogHeader, AlertDialogTitle, AlertDialogDescription,
  AlertDialogFooter, AlertDialogCancel, AlertDialogAction,
} from "@/components/ui/alert-dialog";
import { Plus, DotsThreeVertical, FilePdf, PaperPlaneTilt, Signature, PencilSimple, Trash, Prohibit, SealCheck, Handshake, PencilLine, ArrowsClockwise, CalendarX, UploadSimple, DownloadSimple, Warning, PauseCircle } from "@phosphor-icons/react";

const daysUntil = (iso) => iso ? Math.ceil((new Date(iso) - new Date()) / 86400000) : null;

export function CBadge({ status }) {
  const s = CSTATUS[status] || CSTATUS.draft;
  return <span data-testid={`cstatus-${status}`} className={`inline-flex items-center rounded-sm border px-2 py-0.5 text-xs font-medium ${s.cls}`}>{s.label}</span>;
}

export default function Contracts() {
  const [searchParams, setSearchParams] = useSearchParams();
  const expiringOnly = searchParams.get("echeance") === "1";
  const [contracts, setContracts] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [amending, setAmending] = useState(null);
  const [renewDlg, setRenewDlg] = useState(null);
  const [delId, setDelId] = useState(null);
  const [sendTarget, setSendTarget] = useState(null);
  const [pausing, setPausing] = useState(null);
  const [termId, setTermId] = useState(null);
  const [signDlg, setSignDlg] = useState(null); // {contract, mode: "company"|"manual"}
  const [signName, setSignName] = useState("");
  const fileRef = useRef(null);
  const [uploadFor, setUploadFor] = useState(null);

  const load = () => api.get("/contracts").then((r) => setContracts(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const viewPdf = (c) => window.open(`${API}/contracts/${c.id}/pdf?auth=${localStorage.getItem("token")}`, "_blank");
  const viewSigned = (c) => window.open(`${API}/contracts/${c.id}/signed-file?auth=${localStorage.getItem("token")}`, "_blank");
  const pickFile = (c) => { setUploadFor(c); setTimeout(() => fileRef.current?.click(), 0); };
  const onFile = async (e) => {
    const f = e.target.files?.[0]; e.target.value = "";
    if (!f || !uploadFor) return;
    const fd = new FormData(); fd.append("file", f);
    toast.loading("Téléversement…", { id: "up" });
    try {
      const { data } = await api.post(`/contracts/${uploadFor.id}/signed-file`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success(data.status === "active" && uploadFor.status !== "active" ? "Contrat signé téléversé — contrat activé et récurrence créée" : "Contrat signé téléversé", { id: "up" }); load();
    } catch (err) { toast.error(apiError(err), { id: "up" }); }
  };
  const setRenewal = async (c, auto_renew, notify) => {
    try {
      const { data } = await api.post(`/contracts/${c.id}/renewal`, { auto_renew, notify });
      toast.success(auto_renew ? "Renouvellement automatique réactivé" : data.notified ? `Avis de non-renouvellement envoyé à ${data.notified}` : "Renouvellement automatique désactivé");
      setRenewDlg(null); load();
    } catch (e) { toast.error(apiError(e)); }
  };
  const send = (c) => setSendTarget({ kind: "contract", doc: c });
  const openSign = (c, mode) => { setSignName(mode === "company" ? (c.company_rep || "") : (c.client_rep || "")); setSignDlg({ contract: c, mode }); };
  const confirmSign = async () => {
    const { contract, mode } = signDlg;
    const url = mode === "company" ? `/contracts/${contract.id}/sign-company` : `/contracts/${contract.id}/mark-signed`;
    try {
      const { data } = await api.post(url, { name: signName, accepted: true });
      toast.success(data.status === "active" ? `Contrat ${data.contract_number} activé — récurrence mensuelle créée${data.setup_invoice_number ? ` et facture ${data.setup_invoice_number} (frais non-récurrents) générée` : ""}` : "Signature enregistrée");
      setSignDlg(null); load();
    } catch (e) { toast.error(apiError(e)); }
  };
  const terminate = async () => { try { await api.post(`/contracts/${termId}/terminate`); toast.success("Contrat résilié — récurrence arrêtée"); setTermId(null); load(); } catch (e) { toast.error(apiError(e)); } };
  const del = async () => { try { await api.delete(`/contracts/${delId}`); toast.success("Contrat supprimé"); setDelId(null); load(); } catch (e) { toast.error(apiError(e)); } };

  const sigState = (c) => {
    const s = c.signatures || {};
    const co = !!s.company?.signed_at, cl = !!s.client?.signed_at;
    if (co && cl) return "2/2";
    if (co || cl) return "1/2";
    return "0/2";
  };

  const shown = expiringOnly ? contracts.filter((c) => c.status === "active" && daysUntil(c.end_date) !== null && daysUntil(c.end_date) <= 30) : contracts;
  const expiringCount = contracts.filter((c) => c.status === "active" && daysUntil(c.end_date) !== null && daysUntil(c.end_date) <= 30).length;

  return (
    <div className="space-y-6" data-testid="contracts-page">
      <input ref={fileRef} type="file" accept=".pdf,.png,.jpg,.jpeg" className="hidden" data-testid="signed-file-input" onChange={onFile} />
      <div className="flex items-end justify-between flex-wrap gap-4">
        <div>
          <h1 className="font-heading text-3xl md:text-4xl font-bold tracking-tighter">Contrats de service</h1>
          <p className="text-muted-foreground mt-1">Contrats de solutions globales (Internet, téléphonie) avec facturation mensuelle récurrente.</p>
        </div>
        <Button data-testid="new-contract-btn" className="rounded-sm" onClick={() => { setEditing(null); setOpen(true); }}><Plus size={18} className="mr-1" /> Nouveau contrat</Button>
      </div>

      {(expiringCount > 0 || expiringOnly) && (
        <div data-testid="expiring-banner" className={`border rounded-sm p-4 flex items-center justify-between gap-4 flex-wrap ${expiringOnly ? "border-primary/40 bg-primary/5" : "border-amber-300 bg-amber-50"}`}>
          <div className="flex items-center gap-3"><Warning size={22} className="text-amber-600" />
            <div><div className="font-medium">{expiringOnly ? `Contrats arrivant à échéance dans 30 jours (${shown.length})` : `${expiringCount} contrat${expiringCount > 1 ? "s" : ""} arrive${expiringCount > 1 ? "nt" : ""} à échéance dans les 30 prochains jours`}</div>
              <div className="text-sm text-muted-foreground">Selon l'article 2, un avis de non-renouvellement doit être envoyé au moins 30 jours avant la fin de la durée minimale ; sinon le contrat se renouvelle automatiquement.</div></div>
          </div>
          <Button data-testid="toggle-expiring-filter" variant="outline" size="sm" className="rounded-sm" onClick={() => setSearchParams(expiringOnly ? {} : { echeance: "1" })}>{expiringOnly ? "Voir tous les contrats" : "Voir les échéances"}</Button>
        </div>
      )}

      <div className="border border-border bg-card rounded-sm overflow-x-auto">
        <Table>
          <TableHeader>
            <TableRow className="bg-muted/50">
              <TableHead>Numéro</TableHead>
              <TableHead>Client</TableHead>
              <TableHead>Durée</TableHead>
              <TableHead>Début → Fin</TableHead>
              <TableHead className="text-right">Mensuel</TableHead>
              <TableHead className="text-right">Non-récurrent</TableHead>
              <TableHead>Signatures</TableHead>
              <TableHead>Statut</TableHead>
              <TableHead className="text-right">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {shown.length === 0 && <TableRow><TableCell colSpan={9} className="text-center text-muted-foreground py-10">{expiringOnly ? "Aucun contrat n'arrive à échéance dans les 30 prochains jours." : "Aucun contrat. Créez votre premier contrat de service."}</TableCell></TableRow>}
            {shown.map((c) => {
              const dLeft = daysUntil(c.end_date);
              const soon = c.status === "active" && dLeft !== null && dLeft <= 30;
              return (
              <TableRow key={c.id} data-testid={`contract-row-${c.id}`} className={soon ? "bg-amber-50/60" : ""}>
                <TableCell className="font-mono font-medium">{c.contract_number}{c.amendment_no && <span className="ml-1 text-xs text-muted-foreground font-sans">am. {c.amendment_no}</span>}</TableCell>
                <TableCell className="font-medium">{c.client_info?.legal_name || c.client?.name}</TableCell>
                <TableCell className="text-sm">{c.term_months} mois<span className="block text-xs text-muted-foreground">{c.auto_renew ? "renouv. auto" : "sans renouvellement"}</span>{c.pauses?.length > 0 && <span data-testid={`contract-pauses-badge-${c.id}`} className="inline-flex items-center gap-1 mt-1 rounded-sm border border-sky-200 bg-sky-50 text-sky-800 px-1.5 py-0.5 text-[10px] font-medium"><PauseCircle size={11} /> {c.pauses.length} pause{c.pauses.length > 1 ? "s" : ""}</span>}</TableCell>
                <TableCell className="text-muted-foreground text-sm">{dateFr(c.start_date)} → {dateFr(c.end_date)}{soon && <span data-testid={`contract-expiring-${c.id}`} className="block text-xs text-amber-700 font-medium">{dLeft <= 0 ? "Échéance atteinte" : `Échéance dans ${dLeft} j`}</span>}</TableCell>
                <TableCell className="text-right tabular-nums">{money(c.monthly_total)}</TableCell>
                <TableCell className="text-right tabular-nums text-muted-foreground">{money(c.one_time_total)}</TableCell>
                <TableCell className="text-sm tabular-nums" data-testid={`contract-sigs-${c.id}`}>{sigState(c)}</TableCell>
                <TableCell><CBadge status={c.status} /></TableCell>
                <TableCell className="text-right">
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild><Button data-testid={`contract-menu-${c.id}`} variant="ghost" size="icon" className="h-8 w-8"><DotsThreeVertical size={18} /></Button></DropdownMenuTrigger>
                    <DropdownMenuContent align="end" className="rounded-sm">
                      <DropdownMenuItem data-testid={`contract-pdf-${c.id}`} onClick={() => viewPdf(c)}><FilePdf size={16} className="mr-2" /> Voir le PDF</DropdownMenuItem>
                      {c.signed_file && <DropdownMenuItem data-testid={`contract-signed-download-${c.id}`} onClick={() => viewSigned(c)}><DownloadSimple size={16} className="mr-2" /> Contrat signé (téléversé)</DropdownMenuItem>}
                      {["draft", "sent", "active"].includes(c.status) && <DropdownMenuItem data-testid={`contract-upload-signed-${c.id}`} onClick={() => pickFile(c)}><UploadSimple size={16} className="mr-2" /> {c.signed_file ? "Remplacer le contrat signé" : "Téléverser le contrat signé (PDF)"}</DropdownMenuItem>}
                      {["draft", "sent"].includes(c.status) && <>
                        <DropdownMenuItem data-testid={`contract-send-${c.id}`} onClick={() => send(c)}><PaperPlaneTilt size={16} className="mr-2" /> {c.status === "sent" ? "Renvoyer au client (courriel)" : "Envoyer au client (courriel)"}</DropdownMenuItem>
                        {!c.signatures?.company?.signed_at && <DropdownMenuItem data-testid={`contract-sign-company-${c.id}`} onClick={() => openSign(c, "company")}><Signature size={16} className="mr-2" /> Signer (entreprise)</DropdownMenuItem>}
                        <DropdownMenuItem data-testid={`contract-mark-signed-${c.id}`} onClick={() => openSign(c, "manual")}><SealCheck size={16} className="mr-2" /> Marquer signé par le client (activer)</DropdownMenuItem>
                        <DropdownMenuItem data-testid={`contract-edit-${c.id}`} onClick={() => { setEditing(c); setOpen(true); }}><PencilSimple size={16} className="mr-2" /> Modifier</DropdownMenuItem>
                      </>}
                      {c.status === "active" && <>
                        <DropdownMenuItem data-testid={`contract-amend-${c.id}`} onClick={() => setAmending(c)}><PencilLine size={16} className="mr-2" /> Amender (ajouter / modifier des services)</DropdownMenuItem>
                        <DropdownMenuItem data-testid={`contract-pauses-${c.id}`} onClick={() => setPausing(c)}><PauseCircle size={16} className="mr-2" /> Périodes de pause (suspension saisonnière)</DropdownMenuItem>
                        {c.auto_renew
                          ? <DropdownMenuItem data-testid={`contract-no-renew-${c.id}`} onClick={() => setRenewDlg(c)}><CalendarX size={16} className="mr-2" /> Ne pas renouveler (envoyer l'avis)</DropdownMenuItem>
                          : <DropdownMenuItem data-testid={`contract-renew-${c.id}`} onClick={() => setRenewal(c, true, false)}><ArrowsClockwise size={16} className="mr-2" /> Réactiver le renouvellement auto</DropdownMenuItem>}
                        <DropdownMenuItem data-testid={`contract-terminate-${c.id}`} onClick={() => setTermId(c.id)}><Prohibit size={16} className="mr-2" /> Résilier</DropdownMenuItem>
                      </>}
                      {c.status !== "active" && <><DropdownMenuSeparator /><DropdownMenuItem data-testid={`contract-delete-${c.id}`} className="text-destructive" onClick={() => setDelId(c.id)}><Trash size={16} className="mr-2" /> Supprimer</DropdownMenuItem></>}
                    </DropdownMenuContent>
                  </DropdownMenu>
                </TableCell>
              </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </div>

      <ContractForm open={open} onOpenChange={setOpen} contract={editing} onSaved={load} />
      <AmendDialog contract={amending} onOpenChange={(v) => !v && setAmending(null)} onSaved={load} />

      <AlertDialog open={!!renewDlg} onOpenChange={(v) => !v && setRenewDlg(null)}>
        <AlertDialogContent className="rounded-sm">
          <AlertDialogHeader>
            <AlertDialogTitle>Ne pas renouveler le contrat {renewDlg?.contract_number} ?</AlertDialogTitle>
            <AlertDialogDescription>La facturation mensuelle cessera à la fin de la durée minimale ({dateFr(renewDlg?.end_date)}). Un avis de non-renouvellement peut être envoyé par courriel au client, tel que prévu à l'article 2.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter className="flex-wrap gap-2">
            <AlertDialogCancel className="rounded-sm">Annuler</AlertDialogCancel>
            <Button data-testid="no-renew-silent-btn" variant="outline" className="rounded-sm" onClick={() => setRenewal(renewDlg, false, false)}>Sans avis</Button>
            <AlertDialogAction data-testid="no-renew-notify-btn" className="rounded-sm" onClick={(e) => { e.preventDefault(); setRenewal(renewDlg, false, true); }}>Envoyer l'avis au client</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <Dialog open={!!signDlg} onOpenChange={(v) => !v && setSignDlg(null)}>
        <DialogContent className="rounded-sm max-w-md">
          <DialogHeader>
            <DialogTitle className="font-heading flex items-center gap-2"><Handshake size={20} /> {signDlg?.mode === "company" ? "Signature de l'entreprise" : "Contrat signé par le client"}</DialogTitle>
            <DialogDescription>
              {signDlg?.mode === "company"
                ? "Votre signature électronique sera horodatée et imprimée sur le contrat. Le contrat s'activera dès que le client aura signé."
                : "Utilisez cette option lorsque vous avez reçu le contrat signé (papier / PDF). Le contrat devient actif : la récurrence mensuelle est créée et une facture des frais non-récurrents est générée (brouillon)."}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-1.5">
            <Label className="text-xs uppercase tracking-[0.15em] text-muted-foreground">{signDlg?.mode === "company" ? "Nom du représentant autorisé" : "Nom du signataire (client)"}</Label>
            <Input data-testid="sign-name-input" className="rounded-sm" value={signName} onChange={(e) => setSignName(e.target.value)} />
          </div>
          <DialogFooter>
            <Button variant="outline" className="rounded-sm" onClick={() => setSignDlg(null)}>Annuler</Button>
            <Button data-testid="confirm-sign-btn" className="rounded-sm" onClick={confirmSign}>{signDlg?.mode === "company" ? "Signer" : "Confirmer et activer"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <AlertDialog open={!!termId} onOpenChange={(v) => !v && setTermId(null)}>
        <AlertDialogContent className="rounded-sm">
          <AlertDialogHeader><AlertDialogTitle>Résilier ce contrat ?</AlertDialogTitle><AlertDialogDescription>La récurrence mensuelle liée sera mise en pause. Les factures déjà émises sont conservées.</AlertDialogDescription></AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="rounded-sm">Annuler</AlertDialogCancel>
            <AlertDialogAction data-testid="confirm-terminate-contract" className="rounded-sm bg-destructive hover:bg-destructive/90" onClick={terminate}>Résilier</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <SendDialog target={sendTarget} onOpenChange={(v) => !v && setSendTarget(null)} onSent={load} />
      <PausesDialog contract={pausing} onOpenChange={(v) => !v && setPausing(null)} onSaved={load} />
      <AlertDialog open={!!delId} onOpenChange={(v) => !v && setDelId(null)}>
        <AlertDialogContent className="rounded-sm">
          <AlertDialogHeader><AlertDialogTitle>Supprimer ce contrat ?</AlertDialogTitle><AlertDialogDescription>Cette action est irréversible.</AlertDialogDescription></AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="rounded-sm">Annuler</AlertDialogCancel>
            <AlertDialogAction data-testid="confirm-delete-contract" className="rounded-sm bg-destructive hover:bg-destructive/90" onClick={del}>Supprimer</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
